import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiClient } from '../services/http.js';
import { useAuth } from '../hooks/useAuth.jsx';

/**
 * Home autenticada (P17, redesenhada em P23G): painel de continuidade —
 * Leitura Assistida é a ação principal (linha 1, 8 colunas), Liturgia do
 * dia acompanha ao lado (4 colunas); conversa/trilha/caderno formam uma
 * segunda linha de continuidade; progresso e sugestão fecham a terceira
 * linha. Plano/créditos não aparecem mais como card — já vivem no bloco de
 * conta do App Shell (P23E). Cada card carrega e falha de forma
 * independente — a falha de um endpoint não derruba os demais.
 */

const SEASON_LABELS = {
  advento: 'Advento',
  natal: 'Natal',
  quaresma: 'Quaresma',
  pascoa: 'Páscoa',
  comum: 'Tempo Comum',
};

function useCard(loader) {
  const [state, setState] = useState({ status: 'loading', data: null });

  useEffect(() => {
    let cancelled = false;
    loader()
      .then((data) => {
        if (!cancelled) setState({ status: 'ok', data });
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'error', data: null });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return state;
}

function CardShell({ title, icon, children, to, linkLabel, span = '', highlight = false }) {
  return (
    <div
      className={`bg-surface-container-low border rounded-xl p-5 flex flex-col gap-3 ${span} ${
        highlight ? 'border-primary/40' : 'border-outline-variant'
      }`}
    >
      <div className="flex items-center gap-2">
        <span className="material-symbols-outlined text-primary text-[20px]">{icon}</span>
        <h2 className="font-semibold text-sm uppercase tracking-wide text-on-surface-variant">{title}</h2>
      </div>
      <div className="flex-1">{children}</div>
      {to && (
        <Link to={to} className="text-sm text-primary font-medium hover:underline self-start">
          {linkLabel} →
        </Link>
      )}
    </div>
  );
}

function CardError({ message }) {
  return <p className="text-sm text-on-surface-variant/70">{message}</p>;
}

function LeituraCard() {
  const { status, data } = useCard(async () => {
    const { res, data } = await apiClient.request('/reading/me/dashboard');
    if (!res.ok) throw new Error('reading_dashboard_failed');
    return data;
  });

  const active = Array.isArray(data?.active_enrollments) && data.active_enrollments.length > 0
    ? data.active_enrollments[0]
    : null;

  return (
    <CardShell
      title="Leitura de Hoje"
      icon="menu_book"
      span="md:col-span-2 lg:col-span-8 order-1"
      highlight
      to={active ? `/leitura/${active.enrollment_id}/dia/${active.current_day}` : '/leitura'}
      linkLabel={active ? (active.today?.completed ? 'Ver leitura de hoje' : 'Continuar leitura') : 'Explorar planos de leitura'}
    >
      {status === 'loading' && <CardError message="Carregando..." />}
      {status === 'error' && <CardError message="Não foi possível carregar sua leitura." />}
      {status === 'ok' && !active && (
        <p className="text-sm text-on-surface-variant">
          Você ainda não está em nenhum plano de leitura. Escolha um plano guiado para começar hoje mesmo.
        </p>
      )}
      {status === 'ok' && active && (
        <div>
          <p className="text-xs text-on-surface-variant uppercase tracking-wide mb-1">
            {active.plan_title} · Dia {active.current_day} de {active.duration_days || '?'}
          </p>
          <p className="font-serif text-xl text-on-surface">
            {active.today?.title || 'Leitura de hoje disponível'}
          </p>
          {active.today?.completed && (
            <p className="text-xs text-primary mt-1">✓ Concluída hoje</p>
          )}
        </div>
      )}
    </CardShell>
  );
}

function LiturgiaCard() {
  const today = new Date().toISOString().slice(0, 10);
  const { status, data } = useCard(async () => {
    const { res, data } = await apiClient.request(`/calendar/${today}/readings`);
    if (!res.ok) throw new Error('liturgia_failed');
    return data;
  });

  return (
    <CardShell title="Liturgia do Dia" icon="church" span="md:col-span-2 lg:col-span-4 order-2">
      {status === 'loading' && <CardError message="Carregando..." />}
      {status === 'error' && <CardError message="Não foi possível carregar a liturgia de hoje." />}
      {status === 'ok' && (
        <div>
          <p className="font-serif italic text-lg text-on-surface">
            {data?.saint_day || SEASON_LABELS[data?.season] || 'Tempo Comum'}
          </p>
          {data?.season && (
            <p className="text-sm text-on-surface-variant mt-1">
              {SEASON_LABELS[data.season] || data.season}
              {data.is_feast ? ' · Festa' : ''}
            </p>
          )}
        </div>
      )}
    </CardShell>
  );
}

function ConversaCard() {
  const { status, data } = useCard(async () => {
    const { res, data } = await apiClient.getConversations();
    if (!res.ok) throw new Error('conversations_failed');
    return data;
  });

  const last = Array.isArray(data) && data.length > 0 ? data[0] : null;

  return (
    <CardShell
      title="Continuar Conversa"
      icon="chat"
      span="md:col-span-2 lg:col-span-4 order-3"
      to="/chat"
      linkLabel={last ? 'Continuar conversa' : 'Iniciar conversa'}
    >
      {status === 'loading' && <CardError message="Carregando..." />}
      {status === 'error' && <CardError message="Não foi possível carregar suas conversas." />}
      {status === 'ok' && !last && (
        <p className="text-sm text-on-surface-variant">Você ainda não iniciou nenhuma conversa.</p>
      )}
      {status === 'ok' && last && (
        <div>
          <p className="text-on-surface font-medium truncate">{last.title || 'Conversa sem título'}</p>
          <p className="text-xs text-on-surface-variant mt-1">
            {new Date(last.created_at).toLocaleDateString('pt-BR')}
          </p>
        </div>
      )}
    </CardShell>
  );
}

function TrilhaCard() {
  const { status, data } = useCard(async () => {
    const { res, data } = await apiClient.request('/trails');
    if (!res.ok) throw new Error('trails_failed');
    return data;
  });

  const active = Array.isArray(data)
    ? data.find((t) => t.subscription && t.subscription.active)
    : null;

  return (
    <CardShell
      title="Trilha Ativa"
      icon="map"
      span="md:col-span-2 lg:col-span-4 order-4"
      to="/trilhas"
      linkLabel={active ? 'Ver trilha' : 'Explorar trilhas'}
    >
      {status === 'loading' && <CardError message="Carregando..." />}
      {status === 'error' && <CardError message="Não foi possível carregar suas trilhas." />}
      {status === 'ok' && !active && (
        <p className="text-sm text-on-surface-variant">Você ainda não está em nenhuma trilha de estudo.</p>
      )}
      {status === 'ok' && active && (
        <div>
          <p className="text-on-surface font-medium truncate">{active.subscription.title}</p>
          <p className="text-xs text-on-surface-variant mt-1">
            Dia {active.subscription.current_day} de {active.subscription.day_count || '?'}
          </p>
        </div>
      )}
    </CardShell>
  );
}

function CadernoCard() {
  const { status, data } = useCard(async () => {
    const { res, data } = await apiClient.request('/notes?limit=1');
    if (!res.ok) throw new Error('notes_failed');
    return data;
  });

  const note = Array.isArray(data) && data.length > 0 ? data[0] : null;

  return (
    <CardShell
      title="Caderno Espiritual"
      icon="auto_stories"
      span="md:col-span-2 lg:col-span-4 order-5"
      to="/caderno"
      linkLabel={note ? 'Ver caderno' : 'Criar primeira anotação'}
    >
      {status === 'loading' && <CardError message="Carregando..." />}
      {status === 'error' && <CardError message="Não foi possível carregar seu caderno." />}
      {status === 'ok' && !note && (
        <p className="text-sm text-on-surface-variant">Você ainda não tem anotações.</p>
      )}
      {status === 'ok' && note && (
        <div>
          <p className="text-on-surface font-medium truncate">{note.title || 'Anotação sem título'}</p>
          <p className="text-xs text-on-surface-variant mt-1">
            {new Date(note.created_at).toLocaleDateString('pt-BR')}
          </p>
        </div>
      )}
    </CardShell>
  );
}

function AtividadeCard() {
  const { status, data } = useCard(async () => {
    const { res, data } = await apiClient.request('/auth/me/insights?days=7');
    if (!res.ok) throw new Error('insights_failed');
    return data;
  });

  const topIntent = data?.by_intent?.find((b) => b.intent === data.most_studied_intent);

  return (
    <CardShell
      title="Resumo de Progresso"
      icon="monitoring"
      span="md:col-span-2 lg:col-span-8 order-6"
      to="/insights"
      linkLabel="Ver painel completo"
    >
      {status === 'loading' && <CardError message="Carregando..." />}
      {status === 'error' && <CardError message="Não foi possível carregar sua atividade." />}
      {status === 'ok' && (!data || data.total_messages === 0) && (
        <p className="text-sm text-on-surface-variant">Sem atividade nos últimos 7 dias.</p>
      )}
      {status === 'ok' && data && data.total_messages > 0 && (
        <div>
          <p className="text-on-surface font-medium">{data.total_messages} mensagens nos últimos 7 dias</p>
          {topIntent && (
            <p className="text-xs text-on-surface-variant mt-1">Tema mais estudado: {topIntent.label}</p>
          )}
        </div>
      )}
    </CardShell>
  );
}

function SugestaoCard() {
  const { status, data } = useCard(async () => {
    const { res, data } = await apiClient.request('/reading/recommendation');
    if (!res.ok) throw new Error('recommendation_failed');
    return data;
  });

  return (
    <CardShell
      title="Próxima Ação Sugerida"
      icon="auto_awesome"
      span="md:col-span-2 lg:col-span-4 order-7"
      to={data?.plan ? '/leitura' : '/trilhas'}
      linkLabel={data?.plan ? 'Ver plano sugerido' : 'Explorar trilhas'}
    >
      {status === 'loading' && <CardError message="Carregando..." />}
      {status === 'error' && <CardError message="Sem sugestão disponível no momento." />}
      {status === 'ok' && data?.plan && (
        <div>
          <p className="text-on-surface font-medium truncate">{data.plan.title}</p>
          {Array.isArray(data.reasons) && data.reasons[0] && (
            <p className="text-xs text-on-surface-variant mt-1">{data.reasons[0]}</p>
          )}
        </div>
      )}
      {status === 'ok' && !data?.plan && (
        <p className="text-sm text-on-surface-variant">Explore uma trilha de estudo para dar o próximo passo.</p>
      )}
    </CardShell>
  );
}

export default function HomePage() {
  const { user } = useAuth();
  const todayLabel = new Date().toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long' });

  return (
    <div className="p-4 md:p-6 max-w-content-dashboard mx-auto">
      <h1 className="text-xl md:text-2xl font-bold text-on-surface mb-1">
        Olá{user?.name ? `, ${user.name.split(' ')[0]}` : ''}
      </h1>
      <p className="text-sm text-on-surface-variant mb-6 capitalize">
        {todayLabel} · Sua jornada de fé, em um só lugar.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-4">
        <LeituraCard />
        <LiturgiaCard />
        <ConversaCard />
        <TrilhaCard />
        <CadernoCard />
        <AtividadeCard />
        <SugestaoCard />
      </div>
    </div>
  );
}
